import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Link, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

import { SearchGroupsQueryVariables, UserFeedFollowGroupFragment } from 'api';
import { CheckedMode, InfiniteQuery, UseCheckedState } from 'types/app';
import {
  DataTable,
  DataTableActions,
  DataTableColumn,
  DataTableRowSelection,
  GroupAvatar,
  Numeric,
} from 'components';
import { generateLink } from 'Links';

import Actions from './Actions';

interface Props extends Partial<UseCheckedState>, InfiniteQuery<UserFeedFollowGroupFragment> {
  variables: SearchGroupsQueryVariables;
}

const AdminGroupsTable: FC<Props> = ({ variables, ...rest }) => {
  const { t } = useTranslation();
  const { count, results, sentinelRef, isExhausted, isInitialLoad } = rest;
  const { setCheckedState, checkedMode, checkedIds, resetCheckedState } = rest;

  const isAllChecked = checkedMode !== CheckedMode.OFF;

  function handleAllCheckedChange(event: ChangeEvent<HTMLInputElement>) {
    setCheckedState(event.target.checked ? CheckedMode.PAGE : CheckedMode.OFF);
  }

  function handleCheckedChange(checkedId: string, checked: boolean) {
    let nextCheckedIds = [];

    if (checkedMode) {
      setCheckedState(CheckedMode.OFF);
      nextCheckedIds = results.map(item => item.id).filter(id => id !== checkedId);
    } else if (checked) {
      nextCheckedIds = checkedIds.slice();
      nextCheckedIds.push(checkedId);
    } else {
      nextCheckedIds = checkedIds.filter(id => id !== checkedId);
    }

    setCheckedState(nextCheckedIds);
  }

  const columns: DataTableColumn<UserFeedFollowGroupFragment>[] = [
    {
      width: 50,
      headerCell: {
        padding: 'checkbox',
        render: <Checkbox checked={isAllChecked} onChange={handleAllCheckedChange} />,
      },
      bodyCell: {
        padding: 'checkbox',
        render: group => (
          <Checkbox
            name={group.id}
            checked={isAllChecked || checkedIds.includes(group.id)}
            onChange={event => handleCheckedChange(group.id, event.target.checked)}
          />
        ),
      },
    },
    {
      width: 72,
      headerCell: {
        render: t('common:name'),
      },
      bodyCell: {
        render: group => <GroupAvatar link size={50} group={group} />,
      },
    },
    {
      width: 400,
      headerCell: {
        render: null,
      },
      bodyCell: {
        render: group => (
          <Grid container alignItems="center" spacing={1}>
            {group.is_private && (
              <Grid>
                <LockIcon />
              </Grid>
            )}
            <Grid>
              <Link
                variant="subtitle2"
                noWrap
                component={generateLink(`/group/${group.id}`)}
                underline="hover"
              >
                {group.title}
              </Link>
            </Grid>
          </Grid>
        ),
      },
    },
    {
      width: 250,
      headerCell: {
        render: t('common:department'),
      },
      bodyCell: {
        render: group => <Typography noWrap>{group.department_title}</Typography>,
      },
    },
    {
      width: 250,
      headerCell: {
        render: t('common:location'),
      },
      bodyCell: {
        render: group => <Typography noWrap>{group.location_title}</Typography>,
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:member_plural'),
      },
      bodyCell: {
        render: group => (
          <Typography noWrap>
            <Numeric value={group.follower_count} />
          </Typography>
        ),
      },
    },
  ];

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const actions: DataTableActions<UserFeedFollowGroupFragment> = group => {
    return (
      <Actions
        variables={variables}
        checkedIds={[group.id]}
        resetCheckedState={resetCheckedState}
      />
    );
  };

  return (
    <DataTable
      pagination={pagination}
      columns={columns}
      actions={actions}
      data={results}
      rowSelection={
        <DataTableRowSelection
          disableMatchAll
          mode={checkedMode}
          noun={t('common:group', { count })}
          count={results.length}
          total={count}
          onSelectAll={setCheckedState}
        />
      }
    />
  );
};

export default AdminGroupsTable;
